import React, { useMemo, useState, useCallback, memo } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { Virtuoso } from "react-virtuoso";
import { EmptyState, Checkbox, IconButton } from "@toddle-edu/ds-web";
import { CaretOpenedFilled, CaretClosedFilled } from "@toddle-edu/ds-icons";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import { useI18n } from "Hooks";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import SchoolHeader from "AppComponents/SharedEntities/common/SchoolHeader";
import { useCoursesContext } from "../../context/CoursesContext";
import { useSchoolCounts } from "../../hooks";
import useServerFilteredCourses from "../../hooks/useServerFilteredCourses";
import {
  getFilteredHierarchy,
  computeEntityCountSubtitle,
} from "../../modules/CoursesUtils";
import CourseRow from "../CourseRow";
import classes from "./CoursesList.scss";

// ─── Checkbox states ──────────────────────────────────────────────────────────

const CHECKBOX_STATE = {
  INDETERMINATE: "INDETERMINATE",
  DETERMINATE: "DETERMINATE",
  EMPTY: "EMPTY",
};

// ─── SchoolHeaderWithCounts ───────────────────────────────────────────────────

const SchoolHeaderWithCounts = memo(
  ({
    schoolId,
    courseIds,
    isCollapsed,
    onToggle,
    selectedCourseIdSet,
    setSelectedCourseList,
    courseDetailsMap,
    viewOnly,
    organizationId,
    selectedUserTypes,
  }) => {
    const { t } = useI18n(["common"]);
    const { counts, loading: countsLoading } = useSchoolCounts({
      organizationId,
      schoolId,
      skip: viewOnly,
    });

    const courseCount = _.size(courseIds);

    const subtitle = useMemo(() => {
      const classesLabel = `${courseCount} ${t("common:classes")}`;
      if (viewOnly) return classesLabel;
      if (countsLoading && !counts) return null;
      const participantSubtitle = computeEntityCountSubtitle(
        counts,
        selectedUserTypes,
        t
      );
      return participantSubtitle
        ? `${classesLabel} | ${participantSubtitle}`
        : classesLabel;
    }, [viewOnly, countsLoading, counts, courseCount, selectedUserTypes, t]);

    const checkboxState = useMemo(() => {
      let checkedCount = 0;
      _.forEach(courseIds, id => {
        if (selectedCourseIdSet.has(id)) checkedCount++;
      });
      if (checkedCount === 0) return CHECKBOX_STATE.EMPTY;
      if (checkedCount === _.size(courseIds)) return CHECKBOX_STATE.DETERMINATE;
      return CHECKBOX_STATE.INDETERMINATE;
    }, [courseIds, selectedCourseIdSet]);

    const handleCheck = useCallback(() => {
      if (checkboxState === CHECKBOX_STATE.DETERMINATE) {
        const courseIdSet = new Set(courseIds);
        setSelectedCourseList(prev =>
          _.filter(prev, item => !courseIdSet.has(item.id))
        );
      } else {
        setSelectedCourseList(prev => {
          const existingIds = new Set(_.map(prev, "id"));
          const toAdd = _.reduce(
            courseIds,
            (acc, id) => {
              if (!existingIds.has(id)) {
                const details = _.get(courseDetailsMap, id, {});
                acc.push({ id, title: details.title || "" });
              }
              return acc;
            },
            []
          );
          return [...prev, ...toAdd];
        });
      }
    }, [checkboxState, courseIds, setSelectedCourseList, courseDetailsMap]);

    return (
      <SchoolHeader
        schoolId={schoolId}
        subtitle={subtitle}
        isSubtitleLoading={!viewOnly && countsLoading && !counts}
        isCollapsed={isCollapsed}
        onCollapseToggle={onToggle}
        viewOnly={viewOnly}
        isChecked={checkboxState === CHECKBOX_STATE.DETERMINATE}
        isIndeterminate={checkboxState === CHECKBOX_STATE.INDETERMINATE}
        onCheck={handleCheck}
      />
    );
  }
);

SchoolHeaderWithCounts.displayName = "SchoolHeaderWithCounts";

// ─── GradeHeader ──────────────────────────────────────────────────────────────

const GradeHeader = memo(
  ({
    gradeId,
    gradeName,
    courseIds,
    isCollapsed,
    onToggle,
    selectedCourseIdSet,
    setSelectedCourseList,
    courseDetailsMap,
    viewOnly,
  }) => {
    const checkboxState = useMemo(() => {
      let checkedCount = 0;
      _.forEach(courseIds, id => {
        if (selectedCourseIdSet.has(id)) checkedCount++;
      });
      if (checkedCount === 0) return CHECKBOX_STATE.EMPTY;
      if (checkedCount === _.size(courseIds)) return CHECKBOX_STATE.DETERMINATE;
      return CHECKBOX_STATE.INDETERMINATE;
    }, [courseIds, selectedCourseIdSet]);

    const handleCheckbox = useCallback(() => {
      if (viewOnly) return;
      if (checkboxState === CHECKBOX_STATE.DETERMINATE) {
        const courseIdSet = new Set(courseIds);
        setSelectedCourseList(prev =>
          _.filter(prev, item => !courseIdSet.has(item.id))
        );
      } else {
        setSelectedCourseList(prev => {
          const existingIds = new Set(_.map(prev, "id"));
          const toAdd = _.reduce(
            courseIds,
            (acc, id) => {
              if (!existingIds.has(id)) {
                const details = _.get(courseDetailsMap, id, {});
                acc.push({ id, title: details.title || "" });
              }
              return acc;
            },
            []
          );
          return [...prev, ...toAdd];
        });
      }
    }, [
      viewOnly,
      checkboxState,
      courseIds,
      setSelectedCourseList,
      courseDetailsMap,
    ]);

    return (
      <div
        className={classes.gradeHeader}
        onClick={viewOnly ? null : handleCheckbox}
        onKeyDown={
          viewOnly ? null : e => onEnterOrSpaceKeyDown(e, handleCheckbox)
        }
        role="button"
        tabIndex={0}
        data-id={gradeId}
      >
        <div className={classes.gradeLabel}>
          {gradeName ? (
            <span className={`text-body ${classes.gradeName}`}>
              {gradeName}
            </span>
          ) : (
            <SkeletonLoader width="120px" height="12px" />
          )}
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="plain"
            size="x-small"
            icon={isCollapsed ? <CaretClosedFilled /> : <CaretOpenedFilled />}
            onClick={e => {
              e.stopPropagation();
              onToggle();
            }}
          />
        </div>
        {viewOnly ? null : (
          <Checkbox
            isChecked={checkboxState === CHECKBOX_STATE.DETERMINATE}
            isIndeterminate={checkboxState === CHECKBOX_STATE.INDETERMINATE}
          />
        )}
      </div>
    );
  }
);

GradeHeader.displayName = "GradeHeader";

// ─── Toggle helper ────────────────────────────────────────────────────────────

const toggleSetItem = (setter, id) => {
  setter(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
};

// ─── CoursesList ──────────────────────────────────────────────────────────────

const CoursesList = ({ testId }) => {
  const { t } = useI18n(["common"]);
  const {
    hierarchy,
    selectedSchoolIds,
    hierarchyLoading,
    courseDetailsMap,
    allCourseIds,
    schools,
    selectedCourseList,
    setSelectedCourseList,
    viewOnly,
    organizationId,
    selectedUserTypes,
    searchText,
    setVisibleCourseIds,
  } = useCoursesContext();

  const {
    courses: serverCourses,
    loading: serverCoursesLoading,
  } = useServerFilteredCourses({ organizationId, searchText });

  const isSearching = Boolean(searchText);

  const [collapsedSchools, setCollapsedSchools] = useState(new Set());
  const [collapsedGrades, setCollapsedGrades] = useState(new Set());

  const filteredHierarchy = useMemo(
    () => getFilteredHierarchy(hierarchy, selectedSchoolIds),
    [hierarchy, selectedSchoolIds]
  );

  const gradeNameMap = useMemo(() => {
    const map = {};
    _.forEach(courseDetailsMap, course => {
      _.forEach(_.get(course, "grades", []), grade => {
        if (grade.id && grade.name && !map[grade.id]) {
          map[grade.id] = grade.name;
        }
      });
    });
    return map;
  }, [courseDetailsMap]);

  const schoolsMap = useMemo(() => _.keyBy(schools, "id"), [schools]);

  // Derive all course IDs per school from hierarchy (for checkbox + count)
  const schoolCourseIdsMap = useMemo(() => {
    const map = {};
    _.forEach(
      hierarchy,
      (grades, schoolId) => {
        const ids = [];
        _.forEach(grades, courseIds => {
          ids.push(...courseIds);
        });
        map[schoolId] = ids;
      },
      {}
    );
    return map;
  }, [hierarchy]);

  const showSchoolHeaders = _.size(schools) > 1;

  const flatItems = useMemo(() => {
    if (hierarchyLoading && _.isEmpty(allCourseIds)) {
      return _.times(6, i => ({
        type: "course",
        key: `skeleton-${i}`,
        courseId: `skeleton-${i}`,
        gradeId: null,
      }));
    }
    if (isSearching && serverCoursesLoading) {
      return _.times(6, i => ({
        type: "course",
        key: `skeleton-${i}`,
        courseId: `skeleton-${i}`,
        gradeId: null,
      }));
    }

    if (isSearching) {
      // Group server-returned courses by school (curriculumProgram.id === schoolId)
      const coursesBySchool = _.groupBy(serverCourses, c =>
        _.get(c, "curriculumProgram.id")
      );
      const items = [];
      _.forEach(coursesBySchool, (courses, schoolId) => {
        if (_.isEmpty(courses)) return;
        if (showSchoolHeaders) {
          const school = _.get(schoolsMap, schoolId, {});
          if (_.isEmpty(school)) return;
          const schoolCourseIds = _.map(courses, "id");
          items.push({
            type: "school",
            key: `school-${schoolId}`,
            schoolId,
            schoolName: _.get(school, "label", schoolId),
            schoolLogo: _.get(school, "logo"),
            schoolCourseIds,
            isCollapsed: false,
          });
        }
        _.forEach(courses, course => {
          items.push({
            type: "course",
            key: `search-${course.id}`,
            courseId: course.id,
            gradeId: null,
          });
        });
      });
      return items;
    }

    const items = [];
    _.forEach(filteredHierarchy, (grades, schoolId) => {
      const schoolCollapsed = collapsedSchools.has(schoolId);
      if (showSchoolHeaders) {
        const school = _.get(schoolsMap, schoolId, {});
        const schoolName = _.get(school, "label", schoolId);
        const schoolLogo = _.get(school, "logo");
        const schoolCourseIds = _.get(schoolCourseIdsMap, schoolId, []);
        items.push({
          type: "school",
          key: `school-${schoolId}`,
          schoolId,
          schoolName,
          schoolLogo,
          schoolCourseIds,
          isCollapsed: schoolCollapsed,
        });
      }
      if (schoolCollapsed) return;
      _.forEach(grades, (courseIds, gradeId) => {
        const isUngraded = gradeId.endsWith("_ungraded");
        const gradeName = isUngraded
          ? t("common:other")
          : _.get(gradeNameMap, gradeId, "");
        const gradeCollapsed = collapsedGrades.has(gradeId);
        items.push({
          type: "grade",
          key: `${schoolId}-${gradeId}`,
          gradeId,
          schoolId,
          gradeName,
          courseIds,
          isCollapsed: gradeCollapsed,
        });
        if (!gradeCollapsed) {
          _.forEach(courseIds, courseId => {
            items.push({
              type: "course",
              key: `${schoolId}-${gradeId}-${courseId}`,
              courseId,
              gradeId,
            });
          });
        }
      });
    });
    return items;
  }, [
    hierarchyLoading,
    allCourseIds,
    isSearching,
    serverCoursesLoading,
    serverCourses,
    filteredHierarchy,
    collapsedSchools,
    collapsedGrades,
    gradeNameMap,
    showSchoolHeaders,
    schoolsMap,
    schoolCourseIdsMap,
    t,
  ]);

  const selectedCourseIdSet = useMemo(
    () => new Set(_.map(selectedCourseList, "id")),
    [selectedCourseList]
  );

  const itemContent = useCallback(
    (_index, item) => {
      if (item.type === "school") {
        return (
          <SchoolHeaderWithCounts
            schoolId={item.schoolId}
            courseIds={item.schoolCourseIds}
            isCollapsed={item.isCollapsed}
            onToggle={() => toggleSetItem(setCollapsedSchools, item.schoolId)}
            selectedCourseIdSet={selectedCourseIdSet}
            setSelectedCourseList={setSelectedCourseList}
            courseDetailsMap={courseDetailsMap}
            viewOnly={viewOnly}
            organizationId={organizationId}
            selectedUserTypes={selectedUserTypes}
          />
        );
      }
      if (item.type === "grade") {
        return (
          <GradeHeader
            gradeId={item.gradeId}
            gradeName={item.gradeName}
            courseIds={item.courseIds}
            isCollapsed={item.isCollapsed}
            onToggle={() => toggleSetItem(setCollapsedGrades, item.gradeId)}
            selectedCourseIdSet={selectedCourseIdSet}
            setSelectedCourseList={setSelectedCourseList}
            courseDetailsMap={courseDetailsMap}
            viewOnly={viewOnly}
          />
        );
      }
      return (
        <div className={classes.courseRow} data-id={item.courseId}>
          <CourseRow courseId={item.courseId} testId={testId} />
        </div>
      );
    },
    [
      selectedCourseIdSet,
      setSelectedCourseList,
      courseDetailsMap,
      viewOnly,
      testId,
      organizationId,
      selectedUserTypes,
    ]
  );

  const handleRangeChanged = useCallback(
    ({ startIndex, endIndex }) => {
      const courseIds = [];
      for (let i = startIndex; i <= endIndex; i++) {
        const item = flatItems[i];
        if (
          item &&
          item.type === "course" &&
          !String(item.courseId).startsWith("skeleton-")
        ) {
          courseIds.push(item.courseId);
        }
      }
      setVisibleCourseIds(courseIds);
    },
    [flatItems, setVisibleCourseIds]
  );

  if (!hierarchyLoading && _.isEmpty(allCourseIds)) {
    return (
      <EmptyState
        dsVersion="2.0"
        illustration={NoSearchResultsIllustration}
        title={t("common:no_courses_found")}
      />
    );
  }

  if (
    !hierarchyLoading &&
    searchText &&
    !serverCoursesLoading &&
    _.isEmpty(serverCourses)
  ) {
    return (
      <EmptyState
        dsVersion="2.0"
        illustration={NoSearchResultsIllustration}
        title={t("common:noResultHeading")}
      />
    );
  }

  return (
    <Virtuoso
      data={flatItems}
      computeItemKey={(_index, item) => item.key}
      itemContent={itemContent}
      increaseViewportBy={400}
      className={classes.virtuosoContainer}
      rangeChanged={handleRangeChanged}
    />
  );
};

CoursesList.propTypes = {
  testId: PropTypes.string,
};

export default CoursesList;
